import { useState } from "react";
import { Check, AlertCircle, Phone, MessageCircle, FileText, X, Pencil, CheckCheck, Ban, Clock3, Copy } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

function EditableCell({ value, onSave, type = "text", placeholder, className, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  const commit = () => {
    setEditing(false);
    if ((draft ?? "") !== (value ?? "")) onSave(draft);
  };

  if (editing) {
    return (
      <Input
        autoFocus
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(value ?? "");
            setEditing(false);
          }
        }}
        placeholder={placeholder}
        className={cn("h-7 text-xs px-1.5", className)}
        data-testid={testid}
      />
    );
  }
  return (
    <button type="button"
      onClick={() => {
        setDraft(value ?? "");
        setEditing(true);
      }}
      className={cn(
        "group flex items-center gap-1 text-right w-full min-h-7 px-1.5 -mx-1.5 rounded hover:bg-stone-100 transition-colors",
        className
      )}
      data-testid={testid ? `${testid}-view` : undefined}
      title={value || ""}
    >
      <span className="flex-1 truncate whitespace-nowrap">{value || <span className="text-stone-400">{placeholder || "לחצי לעריכה"}</span>}</span>
      <Pencil className="h-3 w-3 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}

export const CustomerTable = ({
  customers,
  selectedIds,
  onToggle,
  onToggleAll,
  results = {},
  onUpdateCustomer,
  channel = "whatsapp",
  rawHeaders = null,
  onDeduplicate,
}) => {
  const allSelected = customers.length > 0 && customers.every((c) => selectedIds.has(c.id));
  const isEmail = channel === "email";
  // Email channel: count valid emails. Otherwise: valid phones.
  const validCount = isEmail
    ? customers.filter((c) => c.email && String(c.email).includes("@")).length
    : customers.filter((c) => c.phone_valid).length;

  const update = (customerId, field, value) => {
    if (onUpdateCustomer) onUpdateCustomer(customerId, { [field]: value });
  };

  // Spreadsheet-style column letters. Indexes the visual columns (skipping
  // the row-number/checkbox utility column).
  const colLetters = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];

  // Display the EXACT header the user wrote in their sheet. If a field has
  // no header in the sheet, fall back to the generic word "אחר" so we never
  // duplicate labels invented by the app. Hide columns whose data is empty
  // across the entire batch.
  const h = rawHeaders || {};
  const hasAnyDate = customers.some((c) => c.doc_date);
  const hasAnyDesc = customers.some((c) => c.description && c.description !== "שירות");
  const hasAnyAmount = customers.some((c) => c.amount);

  // Build the column definitions keyed by field — then render in the
  // SHEET'S ORIGINAL LEFT→RIGHT ORDER so the on-screen table mirrors the
  // user's Google Sheet exactly. Falls back to a fixed sensible order if
  // we don't have the order info (older batches).
  const columnDefs = {
    name: { key: "name", label: h.name || "שם", dim: false, hide: false, kind: "known" },
    phone: { key: "phone", label: h.phone || "טלפון", dim: isEmail, hide: !h.phone, kind: "known" },
    email: { key: "email", label: h.email || "אימייל", dim: !isEmail, hide: !h.email, kind: "known" },
    amount: { key: "amount", label: h.amount || "סכום", dim: false, hide: !h.amount && !hasAnyAmount, kind: "known" },
    doc_date: { key: "doc_date", label: h.doc_date || "תאריך", dim: false, hide: !h.doc_date && !hasAnyDate, kind: "known" },
    description: { key: "description", label: h.description || "תיאור", dim: false, hide: !h.description && !hasAnyDesc, kind: "known" },
    tax_id: { key: "tax_id", label: h.tax_id || "ת.ז/ח.פ", dim: false, hide: !h.tax_id, kind: "known" },
  };
  const knownFields = new Set(Object.keys(columnDefs));
  const order = (h.__order__ && h.__order__.length > 0)
    ? h.__order__
    : ["name", "phone", "email", "amount", "doc_date", "description", "tax_id"];
  // Resolve each entry in order to either a known column def OR an
  // "extra" column (sheet header preserved verbatim).
  const headers = order
    .map((key) => {
      if (knownFields.has(key)) return columnDefs[key];
      // Extra column — render values straight from extra_fields[<sheet header>]
      return { key, label: key, dim: false, hide: false, kind: "extra" };
    })
    .filter((col) => col && !col.hide);

  return (
    <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid="customer-table">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-base font-semibold">רשימת לקוחות</h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            סה"כ {customers.length} לקוחות • {validCount} עם {isEmail ? "אימייל תקין" : "מספר טלפון תקין"} • לחצי על כל שדה לעריכה
          </p>
        </div>
        <div className="flex items-center gap-2">
          {onDeduplicate && customers.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDeduplicate}
              className="h-8 text-xs gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
              data-testid="deduplicate-button"
            >
              <Copy className="h-3.5 w-3.5" />
              ניקוי כפולים
            </Button>
          )}
          <span className="text-xs text-muted-foreground">
            נבחרו: <strong className="text-brand">{selectedIds.size}</strong>
          </span>
        </div>
      </div>

      {/* Scrollable container — keeps the table inside the card so the page
          doesn't blow out when there are many rows. */}
      <div className="overflow-x-auto overflow-y-auto max-h-[50vh]">
        <table className="w-full border-collapse text-xs">
          {/* Top instruction row — reminds the user that the first row of
              their sheet must contain the column titles (matches Google Sheets
              layout: row 1 is headers, then column letters above them).  */}
          <thead className="bg-amber-50 border-b border-amber-200 sticky top-0 z-20">
            <tr>
              <th
                colSpan={headers.length + 2}
                className="p-1.5 text-center text-[10px] font-bold text-amber-900"
              >
                <span className="inline-flex items-center gap-1.5">
                  <AlertCircle className="h-3 w-3" />
                  חובה למלא את כותרת הטבלה בשורה 1 בגיליון בדיוק כפי שמופיע למטה
                </span>
              </th>
            </tr>
          </thead>
          {/* Spreadsheet-style column letters (A, B, C…) above the actual header row */}
          <thead className="bg-stone-100">
            <tr>
              <th className="w-8 p-1 text-[9px] font-bold text-stone-400 border-l border-stone-200 bg-stone-200/60">
                {/* row-number / checkbox utility column — no letter */}
              </th>
              {headers.map((h, i) => (
                <th
                  key={`col-${h.key}`}
                  className="p-1 text-center text-[10px] font-mono font-bold text-stone-500 border-l border-stone-200 bg-stone-200/60"
                >
                  {colLetters[i]}
                </th>
              ))}
              <th className="p-1 text-center text-[9px] font-bold text-stone-400 bg-stone-200/60">
                {/* status column — no letter */}
              </th>
            </tr>
          </thead>
          <thead className="bg-stone-50 border-b border-border">
            <tr>
              <th className="p-1.5 w-10 border-l border-stone-200 bg-stone-100/60">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => onToggleAll(!allSelected)}
                  data-testid="select-all-checkbox"
                />
              </th>
              {headers.map((col) => (
                <th
                  key={`hdr-${col.key}`}
                  className={cn(
                    "p-2 text-right text-xs font-bold border-l border-stone-200 whitespace-nowrap",
                    col.dim ? "text-stone-300 bg-stone-50/80" : "text-stone-700"
                  )}
                >
                  <div className="flex items-center gap-1 justify-end">
                    <span>{col.label}</span>
                    {col.dim && (
                      <span
                        className="text-[9px] font-normal text-stone-400 italic shrink-0"
                        title="עמודה זו אינה פעילה במצב הנוכחי"
                      >
                        (לא פעיל)
                      </span>
                    )}
                  </div>
                </th>
              ))}
              <th className="p-2 text-right text-xs font-bold text-stone-500 whitespace-nowrap">סטטוס</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c, idx) => {
              const result = results[c.id];
              // Email mode: row is "selectable" if it has a valid email.
              const rowValid = isEmail
                ? !!(c.email && String(c.email).includes("@"))
                : !!c.phone_valid;
              return (
                <tr
                  key={c.id}
                  className={cn(
                    "border-b border-border hover:bg-stone-50/60 transition-colors",
                    idx === customers.length - 1 && "border-b-0"
                  )}
                  data-testid={`customer-row-${idx}`}
                >
                  {/* Row number + checkbox combined cell (Excel-style) */}
                  <td className="p-1 border-l border-stone-200 bg-stone-50/60 text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="text-[10px] font-mono font-bold text-stone-400">{idx + 1}</span>
                      <Checkbox
                        checked={selectedIds.has(c.id)}
                        onCheckedChange={() => onToggle(c.id)}
                        disabled={!rowValid}
                        data-testid={`customer-checkbox-${idx}`}
                      />
                    </div>
                  </td>

                  {/* Render every header cell — phone & email always shown
                      side-by-side when both exist; the inactive-for-channel one
                      is rendered with a dim style so the user still sees the
                      original sheet content but knows it's not used right now. */}
                  {headers.map((col) => {
                    const dimClass = col.dim ? "opacity-40 bg-stone-50/40" : "";

                    if (col.key === "name") {
                      return (
                        <td key={col.key} className="p-1.5 border-l border-stone-200">
                          <EditableCell
                            value={c.name}
                            onSave={(v) => update(c.id, "name", v)}
                            testid={`customer-name-${idx}`}
                          />
                        </td>
                      );
                    }

                    if (col.key === "phone") {
                      const phoneValid = !!c.phone_valid;
                      return (
                        <td
                          key={col.key}
                          className={cn("p-1.5 border-l border-stone-200", dimClass)}
                        >
                          <div className="flex items-center gap-1.5">
                            {phoneValid ? (
                              <Phone className="h-3.5 w-3.5 text-green-600 shrink-0" />
                            ) : (
                              <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <EditableCell
                                value={c.phone_e164 || c.phone_raw}
                                onSave={(v) => update(c.id, "phone", v)}
                                testid={`customer-phone-${idx}`}
                                className={cn(!phoneValid && !col.dim && "text-red-600")}
                              />
                              {!phoneValid && !col.dim && (
                                <span className="text-[10px] text-red-500 block leading-tight" title={c.phone_error}>מספר לא תקין</span>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    }

                    if (col.key === "email") {
                      const emailValid = !!(c.email && String(c.email).includes("@"));
                      return (
                        <td
                          key={col.key}
                          className={cn("p-1.5 border-l border-stone-200", dimClass)}
                        >
                          <div className="flex items-center gap-1.5">
                            {emailValid ? (
                              <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
                            ) : (
                              <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <EditableCell
                                value={c.email || ""}
                                onSave={(v) => update(c.id, "email", v)}
                                testid={`customer-email-${idx}`}
                                className={cn(!emailValid && !col.dim && "text-red-600")}
                                placeholder="—"
                              />
                              {!emailValid && !col.dim && c.email && (
                                <span className="text-xs text-red-500 block">אימייל לא תקין</span>
                              )}
                            </div>
                          </div>
                        </td>
                      );
                    }

                    if (col.key === "amount") {
                      return (
                        <td key={col.key} className="p-1.5 border-l border-stone-200">
                          <EditableCell
                            value={c.amount}
                            onSave={(v) => update(c.id, "amount", parseFloat(v) || 0)}
                            type="number"
                            testid={`customer-amount-${idx}`}
                            className="font-mono tabular-nums"
                          />
                        </td>
                      );
                    }

                    if (col.key === "doc_date") {
                      return (
                        <td key={col.key} className="p-1.5 whitespace-nowrap border-l border-stone-200">
                          <EditableCell
                            value={c.doc_date || ""}
                            onSave={(v) => update(c.id, "doc_date", v)}
                            type="date"
                            placeholder="—"
                            testid={`customer-date-${idx}`}
                          />
                        </td>
                      );
                    }

                    if (col.key === "description") {
                      return (
                        <td key={col.key} className="p-1.5 border-l border-stone-200 max-w-[160px]">
                          <EditableCell
                            value={c.description}
                            onSave={(v) => update(c.id, "description", v)}
                            testid={`customer-desc-${idx}`}
                          />
                        </td>
                      );
                    }

                    // Extra column — sheet header preserved verbatim. Editable,
                    // saved into customer.extra_fields.<header>
                    if (col.kind === "extra") {
                      const cur = (c.extra_fields || {})[col.key] || "";
                      return (
                        <td key={`extra-${col.key}`} className="p-1.5 border-l border-stone-200 max-w-[120px]">
                          <EditableCell
                            value={cur}
                            onSave={(v) => {
                              if (!onUpdateCustomer) return;
                              onUpdateCustomer(c.id, { extra_fields: { [col.key]: v } });
                            }}
                            testid={`customer-extra-${col.key}-${idx}`}
                          />
                        </td>
                      );
                    }

                    return null;
                  })}
                  <td className="p-1.5">
                    {!result && rowValid && (
                      <Badge variant="outline" className="text-xs">
                        ממתין
                      </Badge>
                    )}
                    {!result && !rowValid && (
                      <Badge variant="outline" className="text-xs border-red-300 text-red-600">
                        לא תקין
                      </Badge>
                    )}
                    {result?.scheduled && (
                      <Badge
                        className="bg-purple-500 hover:bg-purple-500 text-white text-xs gap-1 whitespace-nowrap"
                        data-testid={`status-scheduled-${idx}`}
                      >
                        <Clock3 className="h-3 w-3" />
                        תוזמן ל-{result.scheduled_label}
                      </Badge>
                    )}
                    {(result?.whatsapp_ok || result?.email_ok) && !result.invoice_cancelled && !result.scheduled && (
                      <Badge
                        className="bg-blue-500 hover:bg-blue-500 text-white text-xs gap-1"
                        data-testid={`status-sent-${idx}`}
                      >
                        <CheckCheck className="h-3 w-3" />
                        חשבון #{result.invoice_number} נשלח
                      </Badge>
                    )}
                    {result?.invoice_cancelled && (
                      <Badge
                        variant="destructive"
                        className="text-xs gap-1"
                        data-testid={`status-cancelled-${idx}`}
                      >
                        <Ban className="h-3 w-3" />
                        מסמך בוטל
                      </Badge>
                    )}
                    {result && !result.whatsapp_ok && !result.email_ok && (
                      <div className="space-y-1">
                        <Badge variant="destructive" className="text-xs gap-1" data-testid={`status-failed-${idx}`}>
                          <X className="h-3 w-3" />
                          נכשל
                        </Badge>
                        {result.invoice_error && (
                          <p
                            className="text-[10px] text-red-600 max-w-[200px] truncate"
                            title={result.invoice_error}
                          >
                            {result.invoice_error}
                          </p>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {customers.length === 0 && (
          <div className="p-12 text-center text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>אין לקוחות להצגה</p>
          </div>
        )}
      </div>
    </div>
  );
};
